import {
  Activity,
  AlertTriangle,
  BadgePercent,
  BarChart3,
  BookOpenCheck,
  Briefcase,
  ClipboardCheck,
  Grid3x3,
  LifeBuoy,
  Scale,
  Star,
  Bell,
  Building2,
  CalendarClock,
  CalendarDays,
  CreditCard,
  FileClock,
  Gauge,
  HandCoins,
  History,
  Images,
  LayoutDashboard,
  Megaphone,
  PlugZap,
  Receipt,
  ScrollText,
  Settings,
  ShieldCheck,
  Smartphone,
  Tags,
  Timer,
  Users,
  UsersRound,
  Wallet,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import type { Role, User } from '@/api/types'
import { can, type Permission } from './permissions'

export interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  perm?: Permission
  roles?: Role[]
  /** The screen waits on a backend endpoint (docs/BACKEND_REQUESTS.md). */
  pending?: string
}

export interface NavGroup {
  label: string
  items: NavItem[]
  /** Superadmin-only group. */
  platform?: boolean
}

export const NAV: NavGroup[] = [
  {
    label: 'Platform',
    platform: true,
    items: [
      { to: '/admin', label: 'Platform overview', icon: Gauge },
      { to: '/admin/operators', label: 'Operators', icon: Building2 },
      { to: '/admin/users', label: 'Users', icon: UsersRound },
      { to: '/admin/chargers', label: 'All chargers', icon: PlugZap },
      { to: '/admin/sessions', label: 'All sessions', icon: History },
      { to: '/admin/super-cards', label: 'Super Cards', icon: CreditCard },
      { to: '/admin/transactions', label: 'Transactions', icon: Receipt },
      { to: '/admin/topups', label: 'Online top-ups', icon: Smartphone },
      { to: '/admin/settlements', label: 'Settlements', icon: Wallet },
      { to: '/admin/nea-tariff', label: 'NEA tariff', icon: FileClock },
      { to: '/admin/payouts', label: 'Payouts', icon: BookOpenCheck, pending: 'B17' },
      { to: '/admin/support', label: 'Support inbox', icon: LifeBuoy, pending: 'B19' },
      { to: '/admin/push', label: 'Push notifications', icon: Megaphone },
      { to: '/admin/carousel', label: 'App carousel', icon: Images },
      { to: '/admin/audit', label: 'Audit log', icon: ScrollText },
      { to: '/admin/settings', label: 'Platform settings', icon: Settings },
    ],
  },
  {
    label: 'Overview',
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/live', label: 'Live operations', icon: Activity },
    ],
  },
  {
    label: 'Network',
    items: [
      { to: '/stations', label: 'Stations', icon: Building2 },
      { to: '/chargers', label: 'Chargers', icon: PlugZap },
      { to: '/commissioning', label: 'Commissioning', icon: ClipboardCheck, perm: 'cpo.write' },
      { to: '/reservations', label: 'Reservations', icon: CalendarClock, pending: 'B3' },
      { to: '/load', label: 'Load management', icon: Zap, perm: 'cpo.write', pending: 'B6' },
    ],
  },
  {
    label: 'Charging & money',
    items: [
      { to: '/wallet', label: 'Personal wallet', icon: Wallet },
      { to: '/sessions', label: 'Sessions', icon: History },
      { to: '/anomalies', label: 'Anomalies', icon: AlertTriangle },
      { to: '/pricing', label: 'Pricing plans', icon: Tags, perm: 'pricing.manage' },
      { to: '/payments', label: 'Online top-ups', icon: Smartphone, perm: 'cpo.admin', pending: 'B7' },
      { to: '/promotions', label: 'Promotions', icon: BadgePercent, perm: 'cpo.admin', pending: 'B11' },
      { to: '/fleets', label: 'Fleet accounts', icon: Briefcase, perm: 'cpo.admin', pending: 'B16' },
      { to: '/expenses', label: 'Station expenses', icon: HandCoins, perm: 'expenses.write' },
      { to: '/accounting', label: 'Accounting & payouts', icon: BookOpenCheck, perm: 'cpo.admin' },
    ],
  },
  {
    label: 'Customers',
    items: [
      { to: '/customers', label: 'Customers', icon: Users, perm: 'cpo.admin' },
      { to: '/cards', label: 'RFID cards', icon: CreditCard, perm: 'rfid.manage' },
      { to: '/transactions', label: 'Wallet transactions', icon: Receipt, perm: 'credits.manage' },
      { to: '/support', label: 'Support inbox', icon: LifeBuoy, perm: 'cpo.write', pending: 'B19' },
      { to: '/reviews', label: 'Reviews', icon: Star, perm: 'cpo.admin', pending: 'B20' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/maintenance', label: 'Maintenance', icon: Wrench, pending: 'B4' },
      { to: '/alerts', label: 'Alerts', icon: Bell, perm: 'cpo.write', pending: 'B5' },
      { to: '/logs', label: 'Charger logs', icon: ScrollText },
    ],
  },
  {
    label: 'Reports',
    items: [
      { to: '/daily-report', label: 'Daily report', icon: CalendarDays, perm: 'expenses.write' },
      { to: '/reports', label: 'Revenue & energy', icon: BarChart3, perm: 'analytics.read' },
      { to: '/utilisation', label: 'Utilisation', icon: Grid3x3, perm: 'analytics.read' },
      { to: '/margin', label: 'Margin per station', icon: Scale, perm: 'cpo.admin' },
      { to: '/uptime', label: 'Uptime', icon: Timer, perm: 'analytics.read', pending: 'B10' },
      { to: '/electricity', label: 'Electricity', icon: FileClock, perm: 'electricity.manage' },
      { to: '/settlements', label: 'Settlements', icon: Wallet, perm: 'cpo.admin' },
    ],
  },
  {
    label: 'Organization',
    items: [
      { to: '/team', label: 'Team & access', icon: ShieldCheck, perm: 'cpo.admin' },
      { to: '/settings', label: 'Settings', icon: Settings, perm: 'cpo.admin' },
      { to: '/audit', label: 'Audit log', icon: ScrollText, perm: 'cpo.admin' },
    ],
  },
]

export function visibleNav(user: User | null): NavGroup[] {
  if (!user) return []
  return NAV.filter((g) => !g.platform || user.role === 'superadmin')
    .map((g) => ({ ...g, items: g.items.filter((i) => (!i.perm || can(user, i.perm)) && (!i.roles || i.roles.includes(user.role))) }))
    .filter((g) => g.items.length)
}
